import React, { createContext, useContext, useEffect, useState } from 'react';
import { Branch } from '@/types/database';

export type ThemeKey = 'default' | 'army' | 'navy' | 'marines' | 'airforce' | 'spaceforce' | 'coastguard' | 'other';
export type ThemePreference = 'auto' | ThemeKey;
export type ThemeMode = 'light' | 'dark';

interface ThemeContextValue {
  currentThemeKey: ThemeKey;
  themePreference: ThemePreference;
  themeMode: ThemeMode;
  setThemePreference: (preference: ThemePreference) => void;
  setThemeMode: (mode: ThemeMode) => void;
  applyTheme: (themeKey: ThemeKey) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

// Map branch to theme key
export function branchToThemeKey(branch?: Branch | null): ThemeKey {
  if (!branch) return 'default';
  
  const mapping: Record<Branch, ThemeKey> = {
    'Army': 'army',
    'Navy': 'navy',
    'Marine Corps': 'marines',
    'Air Force': 'airforce',
    'Space Force': 'spaceforce',
    'Coast Guard': 'coastguard',
    'Other': 'other',
  };
  
  return mapping[branch] || 'default';
}

// localStorage keys
const STORAGE_KEY_PREFERENCE = 'veteranos-theme-preference';
const STORAGE_KEY_MODE = 'veteranos-theme-mode';

// Validation helper
function isValidThemePreference(value: string): value is ThemePreference {
  return ['auto', 'default', 'army', 'navy', 'marines', 'airforce', 'spaceforce', 'coastguard', 'other'].includes(value);
}

interface ThemeProviderProps {
  children: React.ReactNode;
  userBranch?: Branch | null;
  savedPreference?: ThemePreference;
  savedMode?: ThemeMode;
  onPreferenceChange?: (preference: ThemePreference) => void;
  onModeChange?: (mode: ThemeMode) => void;
}

export function ThemeProvider({ 
  children, 
  userBranch, 
  savedPreference = 'auto',
  savedMode = 'light',
  onPreferenceChange,
  onModeChange
}: ThemeProviderProps) {
  // Initialize from localStorage first, then fall back to props
  const [themePreference, setThemePreferenceState] = useState<ThemePreference>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEY_PREFERENCE);
      if (saved && isValidThemePreference(saved)) return saved as ThemePreference;
    }
    return savedPreference;
  });

  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEY_MODE);
      if (saved === 'light' || saved === 'dark') return saved;
    }
    return savedMode;
  });
  
  // Resolve the actual theme to apply
  const currentThemeKey: ThemeKey = React.useMemo(() => {
    if (themePreference === 'auto') {
      return branchToThemeKey(userBranch);
    }
    return themePreference;
  }, [themePreference, userBranch]);

  // Apply theme by setting data-theme attribute
  const applyTheme = React.useCallback((themeKey: ThemeKey) => {
    document.documentElement.setAttribute('data-theme', themeKey);
  }, []);

  // Apply dark mode class
  useEffect(() => {
    if (themeMode === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [themeMode]);

  // Persist to localStorage on changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY_PREFERENCE, themePreference);
    }
  }, [themePreference]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY_MODE, themeMode);
    }
  }, [themeMode]);

  // Apply theme whenever it changes
  useEffect(() => {
    applyTheme(currentThemeKey);
  }, [currentThemeKey, applyTheme]);

  const setThemePreference = React.useCallback((preference: ThemePreference) => {
    setThemePreferenceState(preference);
    onPreferenceChange?.(preference);
  }, [onPreferenceChange]);

  const setThemeMode = React.useCallback((mode: ThemeMode) => {
    setThemeModeState(mode);
    onModeChange?.(mode);
  }, [onModeChange]);

  return (
    <ThemeContext.Provider value={{ 
      currentThemeKey, 
      themePreference,
      themeMode,
      setThemePreference,
      setThemeMode,
      applyTheme 
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useAppTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useAppTheme must be used within ThemeProvider');
  }
  return context;
}
